import { useMemo, useState } from "react";
import { GeoJSON, Marker, Polyline, Tooltip } from "react-leaflet";
import bezierSpline from "@turf/bezier-spline";
import { lineString } from "@turf/helpers";
import { icon } from "leaflet";
import hash from "object-hash";

import { Card, Stack, Typography } from "@mui/material";

import { MAP_CONTENTS_OPACITY } from "Configs/map";
import { ISurvey } from "Types/survey";

import { useAppSelector } from "Hooks/useRedux";

import DestinationIcon from "Assets/Images/Survey/destination-icon.svg";
import SourceIcon from "Assets/Images/Survey/source-icon.svg";
import useTravels from "Hooks/useTravels";

const ICON_SIZE = 22;

export default function Travel() {
  const { name } = useAppSelector((state) => state.operation);
  const { visible, markerMode } = useAppSelector((state) => state.travel);

  const [hoveredItem, setHoveredItem] = useState<ISurvey | null>(null);

  const { data } = useTravels();

  const surveyMarkers = useMemo(() => {
    if (!data) return null;

    return data?.map((item: any, indx: number) => {
      if (!item.Result || indx > 100) return;
      const travel = JSON.parse(`{${item.Result}}`);
      const sourceLat = Number(travel.source_lat);
      const sourceLng = Number(travel.source_lng);
      const destinationLat = Number(travel.destination_lat);
      const destinationLng = Number(travel.destination_lng);

      const sourceIcon = icon({ iconUrl: SourceIcon, iconSize: [ICON_SIZE, ICON_SIZE] });
      const destinationIcon = icon({ iconUrl: DestinationIcon, iconSize: [ICON_SIZE, ICON_SIZE] });

      const events = {
        mouseover: () => {
          setHoveredItem(item);
        },
        mouseout: () => {
          setHoveredItem(null);
        },
      };

      const finalResult = [];

      const latLng1 = [sourceLat, sourceLng];
      const latLng2 = [destinationLat, destinationLng];

      const offsetX = latLng2[1] - latLng1[1];
      const offsetY = latLng2[0] - latLng1[0];

      const r1 = Math.sqrt(offsetX ** 2 + offsetY ** 2);
      const theta = Math.atan2(offsetY, offsetX);

      const thetaOffset = 3.14 / 20;

      const r2 = r1 / 2 / Math.cos(thetaOffset);
      const theta2 = theta + thetaOffset;

      const midpointX = r2 * Math.cos(theta2) + latLng1[1];
      const midpointY = r2 * Math.sin(theta2) + latLng1[0];

      const midpointLatLng = [midpointY, midpointX];

      finalResult.push(latLng1, midpointLatLng, latLng2);

      const line = lineString(finalResult.map((latLng) => [latLng[1], latLng[0]]));
      const curved: any = bezierSpline(line, { sharpness: 1, resolution: 10000 });

      const isActive = name === "create-path" || name === "edit-path";

      const tooltipContent = !isActive && (
        <Tooltip direction="top" offset={[0, -20]}>
          <Card sx={{ py: 2, px: 3 }}>
            <Stack alignItems="center">
              <Typography>dvdv</Typography>
            </Stack>
          </Card>
        </Tooltip>
      );

      const opacity = isActive ? MAP_CONTENTS_OPACITY : 1;

      return (
        <>
          {markerMode.includes("source") ? (
            <Marker
              key={`source-${item.id}`}
              position={[sourceLat, sourceLng]}
              icon={sourceIcon}
              eventHandlers={events}
              opacity={opacity}
            >
              {tooltipContent}
            </Marker>
          ) : null}
          {markerMode.includes("destination") ? (
            <Marker
              key={`destination-${travel.id}`}
              position={[destinationLat, destinationLng]}
              icon={destinationIcon}
              eventHandlers={events}
              opacity={opacity}
            >
              {tooltipContent}
            </Marker>
          ) : null}
          {markerMode.length === 2 ? (
            <>
              <Polyline
                positions={[
                  [travel.source_lat, travel.source_lng],
                  [travel.destination_lat, travel.destination_lng],
                ]}
                pathOptions={{ opacity: 0.2, color: "#000", weight: 5 }}
                opacity={opacity}
              />
              <GeoJSON data={curved} pathOptions={{ weight: 5, color: "#0E1232", opacity }} key={hash(curved)} />
            </>
          ) : null}
        </>
      );
    });
  }, [data, visible, markerMode]);

  if (!visible) return <></>;
  return <>{surveyMarkers}</>;
}
